import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UserPlus, Trash2, Shield, Eye, Calculator } from "lucide-react";
import { useAuth } from "../lib/auth";

const ROLE_META = {
  admin: { label: "Admin", icon: Shield, color: "#06B6D4", desc: "Tüm yetkiler, ekip yönetimi, silme" },
  muhasebeci: { label: "Muhasebeci", icon: Calculator, color: "#10B981", desc: "Kayıt oluşturma, güncelleme, raporlar" },
  goruntuleyici: { label: "Görüntüleyici", icon: Eye, color: "#8B5CF6", desc: "Sadece okuma yetkisi, raporları görür" },
};

export default function Team() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", password: "", role: "muhasebeci" });
  const load = () => api.get("/team").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    try {
      await api.post("/team/invite", form);
      toast.success("Ekip üyesi eklendi"); setOpen(false);
      setForm({ email: "", full_name: "", password: "", role: "muhasebeci" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };
  const del = async (id) => {
    if (!window.confirm("Üye kaldırılsın mı?")) return;
    await api.delete(`/team/${id}`); toast.success("Kaldırıldı"); load();
  };

  return (
    <div className="space-y-5 nf-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-num text-slate-500">EKİP</div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Kullanıcılar & Roller</h1>
        </div>
        {user?.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="invite-member-btn" className="bg-gradient-to-r from-cyan-500 to-emerald-500 text-slate-950 font-semibold"><UserPlus className="w-4 h-4 mr-1.5" /> Üye Ekle</Button></DialogTrigger>
            <DialogContent className="bg-[#121722] border-slate-800">
              <DialogHeader><DialogTitle className="font-head">Yeni Ekip Üyesi</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Ad Soyad</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="bg-slate-900/60 border-slate-800 mt-1" /></div>
                <div><Label>E-posta</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-900/60 border-slate-800 mt-1" /></div>
                <div><Label>Geçici Şifre</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-slate-900/60 border-slate-800 mt-1" /></div>
                <div><Label>Rol</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="bg-slate-900/60 border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(ROLE_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={invite} className="w-full bg-gradient-to-r from-cyan-500 to-emerald-500 text-slate-950 font-semibold">Ekle</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {Object.entries(ROLE_META).map(([k, m]) => (
          <div key={k} className="nf-card p-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${m.color}20`, color: m.color }}><m.icon className="w-5 h-5" /></div>
            <div className="font-head font-bold">{m.label}</div>
            <div className="text-xs text-slate-400 mt-1">{m.desc}</div>
          </div>
        ))}
      </div>

      <div className="nf-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/40 border-b border-slate-800">
            <tr className="text-left text-xs uppercase tracking-widest font-mono-num text-slate-500">
              <th className="px-4 py-3">Üye</th><th className="px-4 py-3">E-posta</th><th className="px-4 py-3">Rol</th><th className="px-4 py-3 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => {
              const m = ROLE_META[u.role] || ROLE_META.goruntuleyici;
              return (
                <tr key={u.id} className="border-b border-slate-800/60 hover:bg-slate-800/20">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500/30 to-emerald-500/30 flex items-center justify-center font-head font-bold text-cyan-300 text-xs">{u.full_name?.[0]}</div>
                      <span className="font-medium">{u.full_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{u.email}</td>
                  <td className="px-4 py-3"><span className="nf-chip" style={{ borderColor: `${m.color}50`, background: `${m.color}15`, color: m.color }}><m.icon className="w-3 h-3" />{m.label}</span></td>
                  <td className="px-4 py-3 text-right">
                    {user?.role === "admin" && u.id !== user.id && (
                      <button onClick={() => del(u.id)} className="text-slate-500 hover:text-rose-400 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
